import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { ScrollText, ShieldCheck, CreditCard, Package, AlertTriangle, Scale, UserX, Copyright, Truck, RotateCcw, Shield, Users, FileText, Lock, Bell, Building2, Globe, Search, X } from 'lucide-react';
import { Input } from '../components/ui/input';

const TermsPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  
  const sections = [
    {
      icon: ScrollText,
      title: "1. Introduction & Platform Role",
      content: `Crafters Market ("we," "us," or "our") is an online marketplace platform that connects makers, crafters, artisans, and trade service providers with buyers seeking handmade, handcrafted, and locally-sourced items and services in Australia.

IMPORTANT ROLE DEFINITION - PLEASE READ CAREFULLY:

Crafters Market operates SOLELY as a facilitator and venue for transactions. We are NOT a party to any contract of sale between buyers and sellers. The contract of sale is formed directly and exclusively between the buyer and seller.

As a marketplace facilitator:
• We provide the platform infrastructure for listings and communications
• We do NOT take ownership or possession of any items listed
• We do NOT inspect, verify, or guarantee any products or services
• We are NOT the seller, supplier, or manufacturer of any items
• We do NOT set prices, shipping terms, or return policies for sellers
• The sale agreement is strictly between the buyer and seller

By using our platform, you acknowledge and agree that Crafters Market's role is limited to providing the marketplace venue only.`
    },
    {
      icon: Shield,
      title: "1A. Official Identity & Anti-Impersonation Notice",
      content: `IMPORTANT — PLEASE READ TO PROTECT YOURSELF FROM SCAMS:

The only official identity of Crafters Market is:
• Account name: "Crafters Market"
• Official email: craftersmarketaus@gmail.com
• Website: craftersmarket.com.au

ANY other account, email, message or social media profile claiming to be "Crafters Market", "Crafters Market Support", "Crafters Market Admin", "Crafters Market Team" or any similar variation is NOT us. It is a scam.

We will NEVER:
• Ask you for your password, full credit card number, or two-factor code
• Send links via DM/SMS asking you to "verify" your account
• Demand urgent payment outside of our website (craftersmarket.com.au)
• Use accounts other than the official email above to contact you

To protect our users, registering a Crafters Market account using a name that contains the words "Crafters Market" (or any close variation) is prohibited and is blocked at signup. Any account discovered using such a name will be removed immediately.

LIABILITY DISCLAIMER:
Crafters Market is NOT liable for any loss, damage, fraud, or harm arising from a user interacting with a third party that impersonates Crafters Market, its team, or its representatives — whether on our platform, via email, SMS, social media, or any other channel. If a communication did not come from craftersmarketaus@gmail.com or from within an authenticated session on craftersmarket.com.au, you should treat it as untrusted. If in doubt, contact us directly at craftersmarketaus@gmail.com to verify.`
    },
    {
      icon: ShieldCheck,
      title: "2. Eligibility & Registration",
      content: `To use Crafters Market as a seller, you must:
      
• Be at least 18 years of age
• Have the legal capacity to enter into binding contracts
• Provide accurate and complete registration information
• Items listed must be handmade, handcrafted, restored, or designed by the seller
• Trade service providers must hold appropriate licenses and insurance where required by law

SELLER DISCLOSURE REQUIREMENTS:

You must disclose during registration whether you are:
• A BUSINESS SELLER (regular sales with profit motive, registered business)
• A HOBBYIST/OCCASIONAL SELLER (infrequent, non-commercial sales)

This distinction affects consumer rights under Australian Consumer Law. Business sellers have additional obligations.

BUSINESS SELLERS MUST:
• Provide a valid Australian Business Number (ABN)
• Provide their registered business name
• Provide a business address (not just a PO Box for legal purposes)
• Maintain accurate contact information (email, phone number)

ALL SELLERS MUST:
• Provide accurate contact details including full name, email, phone number, and residential address
• Keep this information current and accurate at all times
• Acknowledge that this information may be disclosed to buyers, regulatory authorities, or courts in the event of disputes or legal proceedings`
    },
    {
      icon: AlertTriangle,
      title: "3. Prohibited Items & Safety Standards",
      content: `The following items are STRICTLY PROHIBITED on Crafters Market:

ILLEGAL & UNSAFE ITEMS:
• Items that violate Australian Consumer Law or mandatory safety standards
• Products banned or recalled by the ACCC (Australian Competition and Consumer Commission)
• Children's products that fail mandatory safety standards (e.g., toys with small parts, non-compliant cot bedding)
• Cosmetics and skincare products containing prohibited substances or without required labelling
• Electrical items that do not meet Australian electrical safety standards
• Food items that do not comply with Food Standards Australia New Zealand requirements
• Items containing asbestos, lead paint, or other hazardous materials
• Weapons, ammunition, or items designed to cause harm

INTELLECTUAL PROPERTY VIOLATIONS:
• Counterfeit or replica items bearing trademarked logos (e.g., Disney, Nike, etc.)
• Items using copyrighted designs, characters, or artwork without a valid license
• Items that infringe on patents, trademarks, or other intellectual property rights
• "Inspired by" or "fan art" items that use protected IP without authorization

OTHER PROHIBITED ITEMS:
• Mass-produced items not made or designed by the seller
• Resold items purchased from wholesalers or retailers
• Items promoting hate, violence, or discrimination
• Stolen goods or items of questionable provenance
• Any item illegal to sell in Australia

MANDATORY SAFETY COMPLIANCE:
Sellers MUST confirm that their products meet all applicable Australian mandatory standards. This is especially important for:
• Skincare, cosmetics, and personal care products
• Items designed for children or babies
• Electrical and electronic items
• Food and edible products
• Furniture and home goods

Crafters Market reserves the right to immediately remove any listing that violates these guidelines and suspend or terminate seller accounts without notice or refund.`
    },
    {
      icon: Package,
      title: "4. Seller Responsibilities & Consumer Guarantees",
      content: `As a seller on Crafters Market, you are bound by Australian Consumer Law (ACL) and agree to:

PRODUCT QUALITY GUARANTEES:
Under the ACL, products must:
• Be of acceptable quality (safe, durable, free from defects)
• Be fit for their disclosed purpose
• Match their description and any sample shown
• Meet any express warranties made by the seller

YOU MAY NOT:
• Have a "no returns" or "no refunds" policy - this is ILLEGAL under ACL if a product is faulty
• Refuse to provide a remedy (refund, replacement, or repair) for products that fail consumer guarantees
• Mislead buyers about their consumer rights
• Disclaim liability for products that cause injury or loss

SELLER OBLIGATIONS:
• Provide accurate, honest, and complete product descriptions
• Upload clear, accurate images that represent the actual item
• Clearly disclose all materials, dimensions, and relevant specifications
• Set fair and transparent pricing including all fees
• Respond to buyer inquiries within 48 hours
• Fulfill orders within the timeframe specified in your listing
• Provide safe packaging appropriate for the item
• Comply with all applicable Australian consumer protection laws
• Maintain appropriate insurance for your business activities where relevant

SAFETY COMPLIANCE DECLARATION:
By listing items, you confirm and warrant that:
• Your products meet all applicable Australian mandatory safety standards
• You have conducted appropriate safety testing where required
• Your products do not pose a risk of injury or harm when used as intended
• You will immediately remove any product that is found to be unsafe or non-compliant`
    },
    {
      icon: CreditCard,
      title: "5. Payment, Fees & Subscription Refund Policy",
      content: `SUBSCRIPTION FEES:
      
• Seller Subscription: $7.99 AUD per month
• Tradie Advertising: $7.99 AUD per month (after 30-day free trial)
• No commission fees on sales - you keep 100% of your sale price

PAYMENT TERMS:
• Subscriptions are billed monthly in advance
• Payments are processed securely through Stripe
• Failed payments may result in account suspension
• Promotional codes may modify these fees as specified

SUBSCRIPTION REFUND POLICY - PLEASE READ:
• We do NOT provide refunds for subscription payments once processed
• If you forget to cancel your subscription before your next billing date, the payment is non-refundable
• You may cancel your subscription at any time through your account settings
• Upon cancellation, you will retain full access until the end of your current billing period
• It is YOUR responsibility to cancel your subscription before your next payment date
• We recommend setting a reminder before your billing date if you intend to cancel
• No exceptions will be made for forgotten cancellations

NOTE: This subscription refund policy applies ONLY to platform subscription fees, NOT to product purchases. Product returns and refunds are governed by Australian Consumer Law and the seller's obligations thereunder.`
    },
    {
      icon: Truck,
      title: "6. Shipping, Handling & Delivery",
      content: `Sellers are solely responsible for:

• Packaging items securely and appropriately for transport
• Shipping items within the timeframe stated in the listing
• Providing accurate shipping costs or offering local pickup options
• Arranging and paying for shipping services
• Providing tracking information where available
• Ensuring items arrive in the condition described
• Complying with any shipping regulations for restricted items

DELIVERY GUARANTEES:
Under Australian Consumer Law, sellers must deliver items:
• Within the timeframe specified, or if no time specified, within a reasonable time
• To the location agreed with the buyer
• In the condition described in the listing

Crafters Market does not handle shipping or logistics on behalf of sellers and is not liable for items lost, damaged, or delayed in transit.`
    },
    {
      icon: RotateCcw,
      title: "7. Returns, Refunds & Dispute Resolution",
      content: `AUSTRALIAN CONSUMER LAW REQUIREMENTS:

Sellers CANNOT exclude, restrict, or modify consumer guarantees under the ACL. This means:

BUYERS ARE ENTITLED TO A REMEDY IF:
• The product has a major failure (refund or replacement, buyer's choice)
• The product has a minor failure (repair within reasonable time, or refund/replacement if repair is not done in reasonable time)
• The product does not match its description
• The product is not fit for its disclosed purpose
• The product is unsafe

"NO RETURNS" POLICIES ARE ILLEGAL:
Sellers may not advertise or enforce "no returns" or "no refunds" policies for products that fail consumer guarantees. Any such policy is void and unenforceable.

CHANGE OF MIND:
Sellers are NOT required to offer refunds for change of mind (where the product matches its description and is not faulty). However, sellers should clearly communicate their change-of-mind policy.

DISPUTE RESOLUTION:
• Buyers and sellers should first attempt to resolve disputes directly
• Crafters Market may, at its discretion, assist in communication but is NOT a party to disputes
• Unresolved disputes may be escalated to Consumer Affairs Victoria, NSW Fair Trading, or the relevant state consumer protection agency
• Legal disputes are subject to the jurisdiction of Australian courts

REPORTING ISSUES:
Users can report listings they believe violate these terms or Australian law. We will investigate reports and may take action including warning the seller, removing listings, or suspending accounts.`
    },
    {
      icon: Copyright,
      title: "8. Intellectual Property",
      content: `SELLER OWNERSHIP:
• Sellers retain full ownership of their original content, designs, and intellectual property
• By listing on Crafters Market, sellers grant us a non-exclusive, royalty-free license to display, promote, and market their listings on our platform and associated marketing channels

INTELLECTUAL PROPERTY COMPLIANCE:
Sellers warrant and guarantee that:
• They own or have valid licenses for all intellectual property in their listings
• Their items do not infringe any third-party trademarks, copyrights, patents, or other IP rights
• They have not used any protected logos, characters, designs, or branding without authorization
• "Fan art," "inspired by," or derivative works comply with applicable IP laws

WARNING REGARDING INFRINGEMENT:
If you list items containing trademarked logos (e.g., Disney, Nike, sports teams) or copyrighted designs without a valid license:
• Your listing will be removed immediately upon notification
• Your account may be suspended or terminated
• You may be held personally liable for infringement
• Intellectual property owners may take legal action directly against you

TAKEDOWN PROCEDURE:
Crafters Market maintains a rapid removal system for IP complaints. We will:
• Respond to valid IP complaints within 48 hours
• Remove infringing content promptly
• Notify the seller of the complaint
• Terminate repeat infringers

We cooperate with IP owners and may disclose seller information in response to valid legal requests.`
    },
    {
      icon: Bell,
      title: "9. Reporting & Content Removal",
      content: `REPORTING MECHANISM:

Crafters Market provides a reporting system for users to flag:
• Listings that may violate safety standards
• Potentially counterfeit or IP-infringing items
• Misleading or fraudulent listings
• Prohibited items or content
• Seller conduct violations

HOW REPORTS ARE HANDLED:
1. Reports are reviewed by our team within 48-72 hours
2. If concerns are identified, the seller will receive a warning notification
3. Sellers must respond to warnings and may need to provide evidence of compliance
4. Listings may be temporarily suspended pending review
5. Non-compliant listings will be permanently removed
6. Repeat violations may result in account suspension or termination

SELLER WARNINGS:
When a listing is reported, the seller will receive a notification explaining:
• The nature of the concern raised
• What standards or requirements may apply
• A request to verify compliance
• Timeframe to respond or make corrections

Sellers are responsible for conducting their own research and ensuring compliance. Crafters Market does not provide legal or compliance advice.

URGENT SAFETY CONCERNS:
Items posing immediate safety risks will be removed immediately without prior notice to the seller.`
    },
    {
      icon: Scale,
      title: "10. Limitation of Liability & Disclaimers",
      content: `IMPORTANT DISCLAIMERS - PLEASE READ CAREFULLY:

Crafters Market is a platform connecting buyers and sellers ONLY. To the maximum extent permitted by law:

PLATFORM ROLE:
• We are NOT a party to any transaction between buyers and sellers
• We do NOT take possession, ownership, or control of listed items
• We do NOT inspect, verify, or guarantee any products or services
• The contract of sale is between buyer and seller only

GENERAL LIABILITY:
• We are NOT liable for the quality, safety, legality, or authenticity of items listed
• We are NOT liable for any damages, losses, injuries, or harm arising from products
• We are NOT liable for financial losses, consequential damages, or loss of profits
• We do NOT guarantee the accuracy of listings, seller claims, or product descriptions

FOOD, EDIBLES & CONSUMABLES:
• We do NOT verify compliance with food safety standards or regulations
• Sellers of edible items are solely responsible for all compliance
• We are NOT responsible for illness, allergic reactions, or health issues
• Buyers purchase food items entirely at their own risk

BUYER-SELLER DISPUTES:
• We are NOT responsible for resolving disputes
• We do NOT mediate, arbitrate, or take sides in disagreements
• Buyers and sellers must resolve disputes directly
• We may investigate complaints but assume NO legal liability

Nothing in these terms excludes or limits liability that cannot be excluded under Australian Consumer Law. Our liability is limited to the maximum extent permitted by law.`
    },
    {
      icon: Lock,
      title: "11. Privacy, Data Collection & Protection",
      content: `DATA COLLECTION NOTICE (Australian Privacy Principle 5):

We collect personal information from sellers including:
• Full legal name
• Email address
• Phone number
• Residential or business address
• ABN (for business sellers)
• Business name (for business sellers)
• Payment information (processed securely by Stripe)

WHY WE COLLECT THIS INFORMATION:
Under Australian Privacy Principle 3, we collect this information because it is reasonably necessary for:
• Operating the marketplace platform
• Facilitating transactions between buyers and sellers
• Contacting sellers regarding their accounts and listings
• Investigating complaints and disputes
• Complying with legal obligations
• Protecting buyers and the platform from fraudulent activity
• Responding to legal requests from courts or regulatory authorities

WHO WE MAY DISCLOSE YOUR INFORMATION TO:
• Buyers (limited contact information for transaction purposes)
• Regulatory authorities (ACCC, state consumer protection agencies) if required
• Courts or legal proceedings in the event of disputes
• Law enforcement if required by law
• Service providers who assist in operating our platform (under strict confidentiality)

DATA SECURITY (Australian Privacy Principle 11):
We implement appropriate security measures to protect your personal information including:
• Encrypted data storage and transmission
• Access controls limiting who can view sensitive information
• Regular security reviews and updates
• Secure data backup procedures

Your full address and sensitive details are NOT publicly displayed but may be disclosed in specific circumstances outlined above.

For full details, please see our Privacy Policy.`
    },
    {
      icon: Building2,
      title: "12. Business Seller Requirements",
      content: `IDENTIFYING BUSINESS SELLERS:

Under Australian Consumer Law, different rules apply to business sellers versus private/hobbyist sellers. You are likely a BUSINESS SELLER if you:
• Make regular sales with a profit motive
• Have registered a business or have an ABN
• Source items for resale
• Operate like a commercial enterprise

BUSINESS SELLER OBLIGATIONS:
If you are a business seller, you MUST:
• Register as a business seller on our platform
• Provide your Australian Business Number (ABN)
• Provide your registered business name
• Provide a business contact address
• Display your ABN and business details on your profile
• Comply with all business registration and tax obligations
• Maintain adequate insurance coverage where appropriate

CONSEQUENCES OF MISREPRESENTATION:
Falsely representing yourself as a hobbyist when you are a business:
• May constitute misleading and deceptive conduct under the ACL
• Could expose you to penalties from the ATO and ACCC
• Will result in immediate account termination
• May result in legal action

We reserve the right to reclassify accounts and require business registration where selling patterns indicate commercial activity.`
    },
    {
      icon: UserX,
      title: "13. Account Suspension & Termination",
      content: `Crafters Market reserves the right to:

• Suspend or terminate accounts at our discretion
• Remove listings that violate these terms
• Refuse service to anyone for any reason
• Disclose seller information to authorities where required

GROUNDS FOR IMMEDIATE TERMINATION:
• Listing prohibited, unsafe, or illegal items
• Intellectual property infringement (especially repeat offenses)
• Fraudulent activity or misrepresentation
• Refusing to comply with consumer guarantee obligations
• Providing false business or personal information
• Repeated complaints from buyers
• Non-payment of subscription fees
• Abusive behavior toward other users or staff
• Failure to respond to compliance warnings

UPON TERMINATION:
• Sellers must fulfill any outstanding orders
• No refunds will be provided for subscription fees
• Seller information may be retained as required by law
• Terminated sellers may not create new accounts

We may report serious violations to relevant authorities including the ACCC, state consumer protection agencies, or law enforcement.`
    },
    {
      icon: Globe,
      title: "14. Australia-Only Platform",
      content: `JURISDICTIONAL NOTICE:

Crafters Market is operated from Australia and is currently available to users within Australia only. Our Terms of Service are designed to comply with Australian Consumer Law and the Privacy Act 1988 (Cth).

The platform, its listings, workshops, and payment processing are intended for Australian residents. Accounts registered from outside Australia may be restricted or removed.

If we expand internationally in the future, these terms will be updated and existing users will be notified.`
    }
  ];

  // Filter sections based on search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;
    
    const query = searchQuery.toLowerCase();
    return sections.filter(section => 
      section.title.toLowerCase().includes(query) ||
      section.content.toLowerCase().includes(query)
    );
  }, [searchQuery, sections]);

  // Common search terms for quick access
  const quickSearchTerms = [
    'refunds', 'returns', 'ABN', 'business', 'privacy', 'data', 
    'prohibited', 'safety', 'copyright', 'shipping', 'payment', 'subscription'
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF9]">
      {/* Header */}
      <div className="bg-gradient-to-r from-sky-500 to-teal-500 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <ScrollText className="h-16 w-16 mx-auto mb-4" />
            <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
              Terms of Service
            </h1>
            <p className="text-xl text-white/90">
              Crafters Market Seller Agreement
            </p>
            <p className="text-sm text-white/70 mt-2">
              Last updated: March 2026
            </p>
          </motion.div>
        </div>
      </div>

      {/* Important Notice */}
      <div className="max-w-4xl mx-auto px-4 pt-8">
        <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-amber-800 mb-2">Important: Please Read Carefully Before Registering</h3>
              <p className="text-amber-700 text-sm">
                By creating a seller account on Crafters Market, you are entering into a legally binding agreement. 
                You must read and understand these Terms of Service in full before registering. These terms include 
                your legal obligations under Australian Consumer Law, safety requirements, intellectual property 
                rules, and data collection practices. If you do not agree to these terms, do not create an account.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Search Section */}
      <div className="max-w-4xl mx-auto px-4 pt-6">
        <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-3 flex items-center gap-2">
            <Search className="h-5 w-5 text-teal-600" />
            Search Terms & Conditions
          </h3>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              type="text"
              placeholder="Search for topics like 'refunds', 'ABN', 'privacy'..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-stone-100 rounded-full"
              >
                <X className="h-4 w-4 text-stone-400" />
              </button>
            )}
          </div>
          
          {/* Quick Search Tags */}
          <div className="mt-3 flex flex-wrap gap-2">
            <span className="text-xs text-stone-500">Quick search:</span>
            {quickSearchTerms.map(term => (
              <button
                key={term}
                onClick={() => setSearchQuery(term)}
                className={`text-xs px-2 py-1 rounded-full transition-colors ${
                  searchQuery.toLowerCase() === term.toLowerCase()
                    ? 'bg-teal-500 text-white'
                    : 'bg-stone-100 text-stone-600 hover:bg-teal-100 hover:text-teal-700'
                }`}
              >
                {term}
              </button>
            ))}
          </div>
          
          {searchQuery && (
            <p className="mt-3 text-sm text-stone-500">
              Found {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} matching "{searchQuery}"
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 mb-8"
        >
          <p className="text-stone-600 leading-relaxed">
            Welcome to Crafters Market. These Terms of Service ("Terms") govern your use of our platform 
            as a seller. By creating an account and listing items for sale, you agree to be bound by these 
            Terms and confirm that you have read and understood all provisions, including your obligations 
            under Australian Consumer Law.
          </p>
        </motion.div>

        <div className="space-y-6">
          {filteredSections.length === 0 ? (
            <div className="bg-white rounded-xl shadow-sm border border-stone-200 p-8 text-center">
              <Search className="h-12 w-12 text-stone-300 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No results found</h3>
              <p className="text-stone-500">
                Try searching for different keywords like "refunds", "ABN", or "privacy"
              </p>
              <button
                onClick={() => setSearchQuery('')}
                className="mt-4 text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear search and show all sections
              </button>
            </div>
          ) : (
            filteredSections.map((section, index) => (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 * Math.min(index, 5) }}
                className="bg-white rounded-xl shadow-sm border border-stone-200 p-6"
              >
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-gradient-to-br from-sky-100 to-teal-100 rounded-lg flex-shrink-0">
                    <section.icon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-xl font-semibold text-stone-900 mb-3" style={{ fontFamily: 'Playfair Display, serif' }}>
                      {section.title}
                    </h2>
                    <div className="text-stone-600 whitespace-pre-line leading-relaxed text-sm">
                      {section.content}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))
          )}
        </div>

        {/* Contact Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.5 }}
          className="bg-gradient-to-r from-sky-50 to-teal-50 rounded-xl p-8 mt-8 text-center"
        >
          <h3 className="text-xl font-semibold text-stone-900 mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            Questions About These Terms?
          </h3>
          <p className="text-stone-600 mb-4">
            If you have any questions about these Terms of Service, please contact us before registering.
          </p>
          <a 
            href="mailto:support@craftersmarket.com.au" 
            className="text-teal-600 hover:text-teal-700 font-medium"
          >
            support@craftersmarket.com.au
          </a>
        </motion.div>

        {/* Agreement Note */}
        <div className="mt-8 p-6 bg-red-50 border-2 border-red-200 rounded-lg">
          <p className="text-red-800 text-sm text-center font-medium">
            By creating a seller account on Crafters Market, you confirm that you have READ, UNDERSTOOD, 
            and AGREE to be bound by these Terms of Service, including all obligations under Australian 
            Consumer Law, safety standards, intellectual property requirements, and data collection practices.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;
